import { useEffect, useRef, useState, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"
import { T } from "../adminTheme"
import { StatusBadge } from "./UserOverviewData"

/**
 * One visual language for every super-admin screen.
 * Rules: a screen is a stack of <Panel>s; lists are <Table>s whose rows open the entity
 * (linkRow); facts are <Facts>; statuses are <StatusBadge>; risky actions sit behind
 * <DangerZone> and never default to a destructive value.
 */
export const C = {
	text: T.text, muted: T.muted, border: T.border, soft: T.surface2, canvas: T.canvas,
	accent: T.accent, accentSoft: T.accentSoft, danger: T.danger, dangerSoft: T.dangerSoft, warn: T.warn, warnSoft: T.warnSoft
} as const

export const Page = styled.div`
	display: grid; gap: 20px; min-width: 0; color: ${T.text}; font-size: 14px; line-height: 1.5;
	animation: bo-rise .32s ${T.ease} both;
	h2 { margin: 0; font-size: 24px; line-height: 1.2; font-weight: 650; letter-spacing: -0.025em; }
	h3 { margin: 0; font-size: 15px; line-height: 1.35; font-weight: 600; letter-spacing: -0.01em; }
	code { font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace; font-size: 12.5px; }
`
const PanelBox = styled.section`
	background: ${T.surface}; border: 1px solid ${T.border}; border-radius: ${T.radiusLg}; padding: 20px;
	min-width: 0; overflow-x: auto; box-shadow: ${T.shadowSm};
`
const PanelHead = styled.header`display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: center; justify-content: space-between; margin-bottom: 16px;`

/** Card with an optional title, one-line hint and right-aligned actions. */
export const Panel = ({ title, hint, actions, children, style }: { title?: ReactNode; hint?: ReactNode; actions?: ReactNode; children?: ReactNode; style?: React.CSSProperties }) =>
	<PanelBox style={style}>
		{(title || actions) && <PanelHead>
			<div style={{ minWidth: 0 }}>{title && <h3>{title}</h3>}{hint && <Muted style={{ display: "block", fontSize: 13, marginTop: 3 }}>{hint}</Muted>}</div>
			{actions && <Row>{actions}</Row>}
		</PanelHead>}
		{children}
	</PanelBox>

export const Muted = styled.span`color: ${T.muted};`
export const Row = styled.div`display: flex; flex-wrap: wrap; gap: 8px; align-items: center;`

/** Filter / form grid: labels above inputs, wraps on narrow screens. */
export const Fields = styled.div`
	display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; align-items: end;
	label { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: ${T.text2}; font-weight: 500; min-width: 0; }
	input, select, textarea {
		width: 100%; box-sizing: border-box; min-height: 38px; border: 1px solid ${T.borderStrong}; border-radius: 8px; padding: 8px 11px;
		color: ${T.text}; background: ${T.surface}; font: inherit; font-size: 14px; box-shadow: ${T.shadowSm};
		transition: border-color .16s ${T.ease}, box-shadow .16s ${T.ease};
	}
	input::placeholder, textarea::placeholder { color: ${T.faint}; }
	input:hover, select:hover, textarea:hover { border-color: #A9A9B5; }
	input:focus-visible, select:focus-visible, textarea:focus-visible { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 3px rgba(80, 70, 229, .14); }
	select { appearance: none; padding-right: 32px; cursor: pointer;
		background: ${T.surface} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236E6E7A' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 11px center; }
`

/** Compact row of labelled filters (native selects/inputs) that wraps on narrow screens. */
export const FilterBar = styled(Fields)`
	display: flex; flex-wrap: wrap; gap: 10px 12px; align-items: flex-end;
	label { flex: 0 1 auto; font-size: 12.5px; color: ${T.muted}; gap: 5px; }
	input, select { min-height: 34px; padding-top: 6px; padding-bottom: 6px; font-size: 13.5px; width: auto; min-width: 140px; }
`

const SearchBox = styled.label`
	position: relative; display: block;
	svg { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: ${T.faint}; pointer-events: none; transition: color .16s; }
	input {
		width: 100%; box-sizing: border-box; height: 44px; padding: 0 14px 0 40px; border: 1px solid ${T.borderStrong}; border-radius: 10px;
		background: ${T.surface}; color: ${T.text}; font: inherit; font-size: 14.5px; box-shadow: ${T.shadowSm};
		transition: border-color .16s ${T.ease}, box-shadow .16s ${T.ease};
	}
	input::placeholder { color: ${T.faint}; }
	input:hover { border-color: #A9A9B5; }
	input:focus { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 4px rgba(80, 70, 229, .12); }
	&:focus-within svg { color: ${T.accent}; }
`
/** The primary search on a list screen: big, with a magnifier, label kept for screen readers. */
export const SearchField = ({ label, placeholder, value, onChange, autoFocus }: { label: string; placeholder?: string; value: string; onChange: (value: string) => void; autoFocus?: boolean }) =>
	<SearchBox>
		<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round'><circle cx='11' cy='11' r='7' /><path d='m20 20-3.5-3.5' /></svg>
		<input type='search' aria-label={label} placeholder={placeholder ?? label} value={value} autoFocus={autoFocus} onChange={(event) => onChange(event.target.value)} />
	</SearchBox>

const AVATAR_HUES = [250, 200, 160, 30, 330, 280, 15, 190]
/** Initials in a soft tinted circle; the hue is stable per name so a client is recognisable at a glance. */
export const Avatar = ({ name, size = 32 }: { name?: string | null; size?: number }) => {
	const text = (name || "?").trim()
	const initials = text.split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase()
	const hue = AVATAR_HUES[[...text].reduce((sum, char) => sum + char.charCodeAt(0), 0) % AVATAR_HUES.length]
	return <span aria-hidden style={{ width: size, height: size, flex: "none", display: "inline-grid", placeItems: "center", borderRadius: size, fontSize: size * 0.38, fontWeight: 600, color: `hsl(${hue} 45% 32%)`, background: `hsl(${hue} 70% 95%)`, boxShadow: `inset 0 0 0 1px hsl(${hue} 50% 88%)` }}>{initials}</span>
}

export const Facts = styled.dl`
	display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px 24px; margin: 0;
	dt { color: ${T.muted}; font-size: 12.5px; }
	dd { margin: 3px 0 0; font-weight: 550; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
`
export const Fact = ({ label, children }: { label: ReactNode; children: ReactNode }) => <div><dt>{label}</dt><dd>{children}</dd></div>

export const Table = styled.table`
	width: 100%; border-collapse: separate; border-spacing: 0; text-align: left; font-size: 13.5px; font-variant-numeric: tabular-nums;
	th { padding: 9px 14px; color: ${T.muted}; background: ${T.surface2}; font-weight: 600; font-size: 11.5px; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; border-top: 1px solid ${T.border}; border-bottom: 1px solid ${T.border}; }
	th:first-child { border-left: 1px solid ${T.border}; border-radius: 8px 0 0 8px; }
	th:last-child { border-right: 1px solid ${T.border}; border-radius: 0 8px 8px 0; }
	td { padding: 13px 14px; border-bottom: 1px solid ${T.border}; vertical-align: middle; transition: background-color .14s ${T.ease}; }
	th:first-child, td:first-child { padding-left: 12px; }
	th:last-child, td:last-child { padding-right: 12px; }
	tbody tr:last-child td { border-bottom: 0; }
	td small { display: block; margin-top: 1px; color: ${T.muted}; font-size: 12.5px; }
	tr[data-link] { cursor: pointer; }
	tr[data-link]:hover td, tr[data-link]:focus-visible td { background: ${T.hover}; }
	tr[data-link]:focus-visible { outline: none; }
	tr[data-link] td:first-child { border-radius: 8px 0 0 8px; }
	tr[data-link] td:last-child { border-radius: 0 8px 8px 0; }
	tr[data-link]:hover [data-open] { color: ${T.accent}; transform: translateX(2px); }
	/* Selected row: a soft tint and a short rounded marker, not a full-height block. */
	tr[data-active] td { background: rgba(80, 70, 229, .055); }
	tr[data-active]:hover td { background: rgba(80, 70, 229, .085); }
	tr[data-active] td:first-child { position: relative; }
	tr[data-active] td:first-child::before {
		content: ""; position: absolute; left: 2px; top: 28%; bottom: 28%; width: 3px; border-radius: 3px; background: ${T.accent};
		animation: bo-fade .2s ${T.ease} both;
	}
`
/** Rows open on click and on Enter, so the whole line is the link. */
export const linkRow = (open: () => void) => ({
	"data-link": true, tabIndex: 0, onClick: open,
	onKeyDown: (event: React.KeyboardEvent) => { if (event.key === "Enter") open() }
})
/** Trailing cell that tells the reader the row opens something. */
export const OpenCell = () => <td style={{ textAlign: "right", whiteSpace: "nowrap", width: 1 }} aria-label='Open'>
	<span data-open style={{ display: "inline-block", color: T.faint, fontSize: 18, lineHeight: 1, transition: `color .14s, transform .14s` }}>›</span>
</td>

/** Underline tabs; the active one is marked by aria-selected. */
export const Tabs = styled.div`
	display: flex; gap: 2px; overflow-x: auto; overflow-y: hidden; box-shadow: inset 0 -1px 0 ${T.border}; margin-bottom: 18px; scrollbar-width: none;
	button {
		position: relative; border: 0; background: none; padding: 10px 12px 12px; font: inherit; font-size: 13.5px; font-weight: 500;
		color: ${T.muted}; cursor: pointer; white-space: nowrap; border-radius: 6px 6px 0 0; transition: color .16s ${T.ease};
	}
	button::after {
		content: ""; position: absolute; left: 10px; right: 10px; bottom: 0; height: 2px; border-radius: 2px;
		background: ${T.text}; transform: scaleX(0); transition: transform .22s ${T.ease};
	}
	button:hover { color: ${T.text}; }
	button[aria-selected="true"] { color: ${T.text}; font-weight: 600; }
	button[aria-selected="true"]::after { transform: scaleX(1); }
`

const Skeleton = styled.div`
	display: grid; gap: 10px;
	i { display: block; height: 14px; border-radius: 6px; background: linear-gradient(90deg, #F0F0F3 0%, #F7F7F9 50%, #F0F0F3 100%); background-size: 200% 100%; animation: bo-shimmer 1.3s linear infinite; }
`
const Notice = styled.div<{ $tone: "danger" | "muted" }>`
	display: flex; gap: 10px; align-items: center; padding: ${({ $tone }) => $tone === "danger" ? "11px 14px" : "28px 16px"};
	justify-content: ${({ $tone }) => $tone === "danger" ? "flex-start" : "center"}; border-radius: ${T.radius};
	color: ${({ $tone }) => $tone === "danger" ? T.danger : T.muted};
	background: ${({ $tone }) => $tone === "danger" ? T.dangerSoft : T.surface2};
	border: 1px ${({ $tone }) => $tone === "danger" ? "solid" : "dashed"} ${({ $tone }) => $tone === "danger" ? T.dangerLine : T.border};
	animation: bo-fade .2s ${T.ease} both;
`

/** Loading / error / empty states, identical everywhere. */
export const State = ({ loading, error, empty, emptyText = "Nothing here yet." }: { loading?: boolean; error?: string | null; empty?: boolean; emptyText?: string }) =>
	loading ? <Skeleton role='status' aria-label='Loading…'><i style={{ width: "92%" }} /><i style={{ width: "78%" }} /><i style={{ width: "85%" }} /></Skeleton>
		: error ? <Notice role='alert' $tone='danger'><b>Could not load.</b> {error}</Notice>
			: empty ? <Notice $tone='muted'>{emptyText}</Notice> : null

const Collapsible = styled.details<{ $danger?: boolean }>`
	border: 1px solid ${({ $danger }) => $danger ? T.dangerLine : T.border}; border-radius: ${T.radiusLg}; background: ${T.surface}; padding: 0; box-shadow: ${T.shadowSm};
	summary { list-style: none; cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 14px 18px; font-weight: 600; color: ${({ $danger }) => $danger ? T.danger : T.text}; border-radius: ${T.radiusLg}; transition: background-color .14s; }
	summary:hover { background: ${({ $danger }) => $danger ? T.dangerSoft : T.surface2}; }
	summary::-webkit-details-marker { display: none; }
	summary::before { content: "›"; display: inline-block; font-size: 18px; line-height: 1; transition: transform .2s ${T.ease}; }
	&[open] summary::before { transform: rotate(90deg); }
	&[open] > div { animation: bo-rise .24s ${T.ease} both; }
`
/** Collapsed block for actions that change money, identity or infrastructure. */
export const DangerZone = ({ title = "Risky actions", hint, children }: { title?: string; hint?: string; children: ReactNode }) => {
	const [open, setOpen] = useState(false)
	return <Collapsible $danger open={open} onToggle={(event) => setOpen((event.target as HTMLDetailsElement).open)}>
		<summary>{title}{hint && <Muted style={{ fontWeight: 400, fontSize: 13 }}>· {hint}</Muted>}</summary>
		<div style={{ display: "grid", gap: 14, padding: "0 18px 18px" }}>{children}</div>
	</Collapsible>
}



const DisclosureBox = styled.details<{ $flat?: boolean }>`
	border: ${({ $flat }) => $flat ? "0" : `1px solid ${T.border}`}; border-radius: ${T.radius}; background: ${({ $flat }) => $flat ? "none" : T.surface};
	transition: border-color .16s ${T.ease};
	&:hover { border-color: ${({ $flat }) => $flat ? "transparent" : T.borderStrong}; }
	> summary {
		list-style: none; cursor: pointer; display: flex; align-items: center; gap: 8px; user-select: none; color: ${T.text2};
		padding: ${({ $flat }) => $flat ? "2px 0" : "10px 14px"}; border-radius: ${T.radius}; font-size: ${({ $flat }) => $flat ? "12.5px" : "13.5px"}; font-weight: 550;
		transition: background-color .14s ${T.ease}, color .14s ${T.ease};
	}
	> summary::-webkit-details-marker { display: none; }
	> summary::before {
		content: ""; flex: none; width: 6px; height: 6px; border-right: 1.6px solid ${T.muted}; border-bottom: 1.6px solid ${T.muted};
		transform: rotate(-45deg); transition: transform .2s ${T.ease};
	}
	&[open] > summary::before { transform: rotate(45deg); }
	> summary:hover { color: ${T.text}; background: ${({ $flat }) => $flat ? "none" : T.hover}; }
	> summary:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
	> .bo-body { padding: ${({ $flat }) => $flat ? "6px 0 2px 14px" : "2px 14px 14px"}; min-width: 0; overflow-x: auto; }
	&[open] > .bo-body { animation: bo-rise .22s ${T.ease} both; }
`

/** Clean expandable section: chevron that turns, hover, content that rises in. `flat` drops the frame for use inside table cells. */
export const Disclosure = ({ title, label, hint, flat, defaultOpen, children }: { title?: ReactNode; label?: ReactNode; hint?: ReactNode; flat?: boolean; defaultOpen?: boolean; children: ReactNode }) =>
	<DisclosureBox $flat={flat} open={defaultOpen || undefined}>
		<summary>{title ?? label}{hint && <Muted style={{ fontWeight: 400, fontSize: 13 }}>· {hint}</Muted>}</summary>
		<div className='bo-body'>{children}</div>
	</DisclosureBox>

type Tone = "success" | "warn" | "danger" | "info"
const TONES: Record<Tone, [string, string, string]> = {
	success: [T.success, T.successSoft, T.successLine],
	warn: [T.warn, T.warnSoft, T.warnLine],
	danger: [T.danger, T.dangerSoft, T.dangerLine],
	info: [T.info, T.infoSoft, T.border]
}
const CalloutBox = styled.div<{ $tone: Tone }>`
	display: grid; gap: 4px; padding: 11px 14px; border-radius: ${T.radius}; font-size: 13.5px; line-height: 1.5; min-width: 0; overflow-wrap: anywhere;
	color: ${({ $tone }) => TONES[$tone][0]}; background: ${({ $tone }) => TONES[$tone][1]};
	box-shadow: inset 0 0 0 1px ${({ $tone }) => TONES[$tone][2]}; animation: bo-fade .2s ${T.ease} both;
	strong { font-weight: 600; }
	ul { margin: 2px 0 0; padding-left: 18px; }
`
/** Tinted message box: use for one sentence of context or a verdict; pass role='alert' for errors, role='status' for results. */
export const Callout = ({ tone = "warn", title, children, ...rest }: { tone?: Tone; title?: ReactNode } & HTMLAttributes<HTMLDivElement>) =>
	<CalloutBox $tone={tone} role={tone === "danger" ? "alert" : undefined} {...rest}>
		{title && <strong>{title}</strong>}
		{children}
	</CalloutBox>

const CheckLabel = styled.label`
	display: inline-flex; align-items: center; gap: 8px; width: fit-content; font-size: 14px; color: ${T.text}; cursor: pointer;
	input { width: 16px; height: 16px; margin: 0; accent-color: ${T.accent}; cursor: pointer; }
	input:disabled { cursor: not-allowed; }
`
/** Checkbox with its text on one row; sits outside of Fields/FilterBar (those restyle every input). */
export const Check = ({ children, ...input }: { children: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, "type">) =>
	<CheckLabel><input type='checkbox' {...input} />{children}</CheckLabel>

const Menu = styled.details`
	position: relative;
	summary { list-style: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; min-height: 32px; padding: 6px 10px 6px 12px; border: 1px solid ${T.borderStrong};
		border-radius: 7px; font-size: 13px; font-weight: 600; background: ${T.surface}; box-shadow: ${T.shadowSm}; transition: background-color .14s, border-color .14s; user-select: none; }
	summary::-webkit-details-marker { display: none; }
	summary:hover { background: ${T.hover}; border-color: #A9A9B5; }
	summary svg { transition: transform .2s ${T.ease}; color: ${T.muted}; }
	&[open] summary svg { transform: rotate(180deg); }
`
/** Rendered into document.body: an animated ancestor (transform) would otherwise become the fixed containing block. z-index sits above the app shell (9999) and its overlays (10000). */
const MenuPanel = styled.div`
	position: fixed; z-index: 10050; min-width: 220px; display: grid; padding: 5px; background: ${T.surface}; color: ${T.text};
	border: 1px solid ${T.border}; border-radius: 10px; box-shadow: ${T.shadowLg}; animation: bo-pop .18s ${T.ease} both;
	font-family: inherit;
	button { text-align: left; border: 0; background: none; padding: 8px 10px; border-radius: 6px; font: inherit; font-size: 13.5px; cursor: pointer; color: ${T.text}; transition: background-color .12s; }
	button:hover { background: ${T.hover}; }
	button[data-danger] { color: ${T.danger}; }
	button[data-danger]:hover { background: ${T.dangerSoft}; }
	hr { border: 0; border-top: 1px solid ${T.border}; margin: 4px 2px; }
`
/** Secondary actions live behind one button so the header stays quiet. Danger items are separated at the bottom. */
export const ActionMenu = ({ items, label = "More actions" }: { items: Array<[string, () => void, boolean?]>; label?: string }) => {
	// Fixed positioning keeps the menu above scroll containers (tables, panels) that would clip it.
	const [place, setPlace] = useState<React.CSSProperties>({})
	const root = useRef<HTMLDetailsElement>(null)
	const panel = useRef<HTMLDivElement>(null)
	const close = () => setPlace({})
	useEffect(() => {
		if (!place.top && !place.bottom) return
		const outside = (event: MouseEvent) => { if (!root.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) close() }
		window.addEventListener("scroll", close, true); window.addEventListener("resize", close); document.addEventListener("mousedown", outside)
		return () => { window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); document.removeEventListener("mousedown", outside) }
	}, [place])
	return <Menu ref={root} open={Boolean(place.top || place.bottom)} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => { event.stopPropagation(); if (event.key === "Escape") close() }}
		onToggle={(event) => { if (!(event.currentTarget as HTMLDetailsElement).open) close() }}>
		<summary onClick={(event) => {
			event.preventDefault()
			if (place.top || place.bottom) return close()
			const rect = event.currentTarget.getBoundingClientRect()
			const right = window.innerWidth - rect.right
			setPlace(window.innerHeight - rect.bottom < 220 ? { bottom: window.innerHeight - rect.top + 6, right } : { top: rect.bottom + 6, right })
		}}>{label}<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'><path d='m6 9 6 6 6-6' /></svg></summary>
		{(place.top || place.bottom) ? createPortal(<MenuPanel ref={panel} role='menu' style={place} onKeyDown={(event) => { if (event.key === "Escape") close() }}>
			{items.map(([text, run, danger], index) => <span key={text} style={{ display: "contents" }}>
				{danger && index > 0 && <hr />}
				<button type='button' role='menuitem' data-danger={danger || undefined} onClick={() => { close(); run() }}>{text}</button>
			</span>)}
		</MenuPanel>, document.body) : null}
	</Menu>
}

const MultiTrigger = styled.button`
	display: flex; align-items: center; gap: 8px; width: 100%; min-height: 38px; box-sizing: border-box; padding: 8px 11px; border: 1px solid ${T.borderStrong}; border-radius: 8px;
	background: ${T.surface}; color: ${T.text}; box-shadow: ${T.shadowSm}; font: inherit; font-size: 14px; font-weight: 400; text-align: left; cursor: pointer;
	transition: border-color .16s ${T.ease}, box-shadow .16s ${T.ease};
	&:hover { border-color: #A9A9B5; }
	&:focus-visible, &[aria-expanded="true"] { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 3px rgba(80, 70, 229, .14); }
	> span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	> b { flex: none; padding: 1px 8px; border-radius: 10px; background: ${T.accentSoft}; color: ${T.accentText}; font-size: 12px; font-weight: 600; }
	> svg { flex: none; color: ${T.muted}; }
`
const MultiPanel = styled.div`
	position: fixed; z-index: 10050; display: flex; flex-direction: column; background: ${T.surface}; color: ${T.text};
	border: 1px solid ${T.border}; border-radius: 10px; box-shadow: ${T.shadowLg}; animation: bo-pop .16s ${T.ease} both; font-size: 13.5px;
	> input { margin: 8px; padding: 7px 10px; border: 1px solid ${T.borderStrong}; border-radius: 7px; font: inherit; color: ${T.text}; background: ${T.surface}; }
	> input:focus-visible { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 3px rgba(80, 70, 229, .14); }
	> div { display: flex; justify-content: space-between; gap: 8px; padding: 0 12px 8px; }
	> div button { border: 0; background: none; padding: 0; font: inherit; font-size: 12.5px; font-weight: 600; color: ${T.accentText}; cursor: pointer; }
	> div button:disabled { color: ${T.faint}; cursor: default; }
	ul { list-style: none; margin: 0; padding: 4px; overflow-y: auto; border-top: 1px solid ${T.border}; }
	label { display: flex; align-items: center; gap: 9px; padding: 7px 9px; border-radius: 7px; cursor: pointer; }
	label:hover { background: ${T.hover}; }
	label input { margin: 0; accent-color: ${T.accent}; }
	p { margin: 0; padding: 8px 10px; color: ${T.muted}; }
`
/** Multi-pick with search. Nothing picked means "all" — the trigger then shows `allLabel`. */
export const MultiSelect = ({ label, allLabel, options, value, onChange }: {
	label: string; allLabel: string; options: Array<{ value: string; label: string }>; value: string[]; onChange: (value: string[]) => void
}) => {
	const [place, setPlace] = useState<React.CSSProperties | null>(null)
	const [query, setQuery] = useState("")
	const trigger = useRef<HTMLButtonElement>(null)
	const panel = useRef<HTMLDivElement>(null)
	const close = () => { setPlace(null); setQuery("") }
	useEffect(() => {
		if (!place) return
		const inside = (target: EventTarget | null) => trigger.current?.contains(target as Node) || panel.current?.contains(target as Node)
		const outside = (event: MouseEvent) => { if (!inside(event.target)) close() }
		const scroll = (event: Event) => { if (!inside(event.target)) close() }
		const key = (event: KeyboardEvent) => { if (event.key === "Escape") close() }
		window.addEventListener("scroll", scroll, true); window.addEventListener("resize", close); document.addEventListener("mousedown", outside); document.addEventListener("keydown", key)
		return () => { window.removeEventListener("scroll", scroll, true); window.removeEventListener("resize", close); document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", key) }
	}, [place])
	const open = () => {
		const rect = trigger.current!.getBoundingClientRect()
		const width = Math.max(rect.width, 280)
		const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))
		const below = window.innerHeight - rect.bottom - 14, above = rect.top - 14
		setPlace(below < 240 && above > below
			? { bottom: window.innerHeight - rect.top + 6, left, width, maxHeight: Math.min(360, above) }
			: { top: rect.bottom + 6, left, width, maxHeight: Math.min(360, below) })
	}
	const chosen = new Set(value)
	const needle = query.trim().toLowerCase()
	const shown = options.filter(option => option.label.toLowerCase().includes(needle))
	const names = options.filter(option => chosen.has(option.value)).map(option => option.label)
	return <>
		<MultiTrigger ref={trigger} type='button' aria-label={label} aria-expanded={Boolean(place)} onClick={() => place ? close() : open()}>
			<span>{names.length ? names.join(", ") : allLabel}</span>
			{names.length > 0 && <b>{names.length}</b>}
			<svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'><path d='m6 9 6 6 6-6' /></svg>
		</MultiTrigger>
		{place && createPortal(<MultiPanel ref={panel} style={place}>
			<input type='search' aria-label={`Search ${label.toLowerCase()}`} placeholder='Search' value={query} autoFocus onChange={event => setQuery(event.target.value)} />
			<div>
				<button type='button' disabled={!shown.length} onClick={() => onChange([...new Set([...value, ...shown.map(option => option.value)])])}>Select shown ({shown.length})</button>
				<button type='button' disabled={!value.length} onClick={() => onChange([])}>Clear — {allLabel.toLowerCase()}</button>
			</div>
			<ul>
				{shown.map(option => <li key={option.value}><label><input type='checkbox' checked={chosen.has(option.value)}
					onChange={() => onChange(chosen.has(option.value) ? value.filter(item => item !== option.value) : [...value, option.value])} />{option.label}</label></li>)}
				{!shown.length && <li><p>Nothing found</p></li>}
			</ul>
		</MultiPanel>, document.body)}
	</>
}

const ISO_COUNTRIES = "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ")
const regionName = (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }) } catch { return null } })()
/** ISO-2 country picker: shows "Latvia (LV)", stores "LV". Unknown stored codes stay selectable. */
export const CountrySelect = ({ value, onChange, disabled, name }: { value: string; onChange: (code: string) => void; disabled?: boolean; name?: string }) => {
	const code = value.trim().toUpperCase()
	const options = ISO_COUNTRIES.map((item) => [item, regionName?.of(item) ?? item] as const).sort((a, b) => a[1].localeCompare(b[1]))
	return <select name={name} value={code} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
		<option value=''>Choose a country…</option>
		{code && !ISO_COUNTRIES.includes(code) && <option value={code}>{code}</option>}
		{options.map(([item, label]) => <option key={item} value={item}>{label} ({item})</option>)}
	</select>
}

const PAGE_SIZES = [25, 50, 100]
/**
 * Client-side paging so long lists never become an endless sheet.
 * Returns the visible slice and a <Pager/> to render under the table (hidden when everything fits).
 */
export const usePaged = <V,>(rows: V[], initialSize = 25) => {
	const [size, setSize] = useState(initialSize)
	const [page, setPage] = useState(0)
	const pages = Math.max(1, Math.ceil(rows.length / size))
	const current = Math.min(page, pages - 1)
	// A new filter or search shrinks the list: start from the first page again.
	useEffect(() => { setPage(0) }, [rows.length])
	const pageRows = rows.slice(current * size, (current + 1) * size)
	const pager = rows.length > PAGE_SIZES[0] ? <Pager total={rows.length} from={current * size + 1} to={current * size + pageRows.length}
		page={current} pages={pages} size={size} onPage={setPage} onSize={(next) => { setSize(next); setPage(0) }} /> : null
	return { pageRows, pager }
}

/** Wrap any table: <Paged rows={list}>{(page) => <Table>…page.map…</Table>}</Paged>. */
export const Paged = <V,>({ rows, size, children }: { rows: V[]; size?: number; children: (pageRows: V[]) => ReactNode }) => {
	const { pageRows, pager } = usePaged(rows, size)
	return <>{children(pageRows)}{pager}</>
}

/** "Showing 26–50 of 312", rows per page, previous / next. Works for client and server paging alike. */
export const Pager = ({ total, from, to, page, pages, size, onPage, onSize }: {
	total: number; from: number; to: number; page: number; pages: number; size?: number; onPage: (page: number) => void; onSize?: (size: number) => void
}) =>
	<Row style={{ justifyContent: "space-between", gap: 12, paddingTop: 14, marginTop: 4, borderTop: `1px solid ${T.border}` }}>
		<Muted style={{ fontSize: 13 }} role='status'>Showing {total ? `${from}–${to}` : 0} of {total}</Muted>
		<Row style={{ gap: 10 }}>
			{onSize && size && <Fields style={{ display: "block" }}><label style={{ flexDirection: "row", alignItems: "center", gap: 8, fontWeight: 400, color: T.muted }}>Rows<select value={size} onChange={(event) => onSize(Number(event.target.value))} style={{ minHeight: 32, width: "auto", padding: "4px 30px 4px 10px" }}>{PAGE_SIZES.map((option) => <option key={option} value={option}>{option}</option>)}</select></label></Fields>}
			<PagerButton type='button' disabled={page === 0} onClick={() => onPage(page - 1)} aria-label='Previous page'>‹</PagerButton>
			<Muted style={{ fontSize: 13, minWidth: 70, textAlign: "center" }}>Page {page + 1} of {pages}</Muted>
			<PagerButton type='button' disabled={page >= pages - 1} onClick={() => onPage(page + 1)} aria-label='Next page'>›</PagerButton>
		</Row>
	</Row>
const PagerButton = styled.button`
	width: 32px; height: 32px; display: grid; place-items: center; border: 1px solid ${T.borderStrong}; border-radius: 7px; background: ${T.surface};
	color: ${T.text}; font-size: 18px; line-height: 1; cursor: pointer; box-shadow: ${T.shadowSm}; transition: background-color .14s;
	&:hover:not(:disabled) { background: ${T.hover}; }
	&:disabled { opacity: .4; cursor: default; }
`

const TopButton = styled.button`
	position: fixed; right: 24px; bottom: 24px; z-index: 10040; height: 40px; padding: 0 14px 0 12px; display: inline-flex; align-items: center; gap: 6px;
	border: 1px solid ${T.borderStrong}; border-radius: 999px; background: ${T.surface}; color: ${T.text}; font: inherit; font-size: 13px; font-weight: 600;
	box-shadow: ${T.shadowLg}; cursor: pointer; animation: bo-pop .2s ${T.ease} both; transition: background-color .14s;
	&:hover { background: ${T.hover}; }
`
/**
 * "Back to top" for long pages. Scroll events don't bubble, so it listens in the capture phase and
 * follows whichever large panel the reader is scrolling (the app shell scrolls a box, not the window).
 */
export const BackToTop = () => {
	const [target, setTarget] = useState<HTMLElement | null>(null)
	useEffect(() => {
		const onScroll = (event: Event) => {
			const element = event.target instanceof HTMLElement ? event.target : document.scrollingElement as HTMLElement | null
			if (!element || element.clientWidth < 600 || element.closest("dialog")) return
			setTarget(element.scrollTop > 900 ? element : null)
		}
		window.addEventListener("scroll", onScroll, true)
		return () => window.removeEventListener("scroll", onScroll, true)
	}, [])
	return target ? <TopButton type='button' onClick={() => target.scrollTo({ top: 0, behavior: "smooth" })} aria-label='Back to top'>
		<svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'><path d='m18 15-6-6-6 6' /></svg>Top
	</TopButton> : null
}

/** A button that reads as a text link (opens a related entity). */
export const LinkButton = styled.button`
	border: 0; padding: 0; background: none; font: inherit; color: ${T.accentText}; font-weight: 550; cursor: pointer; text-align: left;
	border-radius: 4px; transition: color .14s;
	&:hover { color: ${T.accentHover}; text-decoration: underline; text-underline-offset: 3px; }
`

/** "card_topup" → "Card topup" */
export const sentence = (value?: string | null) => value ? (value[0].toUpperCase() + value.slice(1)).replaceAll("_", " ") : "—"
export const date = (value?: string | null) => value ? new Date(value).toLocaleString() : "—"
export { StatusBadge }
/** system/user/list `kyc_state`: buyers and team leads of a company never pass our KYC, so they read "Not needed". */
export const KycState = ({ state, status }: { state?: string; status?: string }) =>
	state === "not_required" ? <Muted>Not needed</Muted> : <StatusBadge value={state || status || "unknown"} />
